import {
  DropDownItem,
  DropDownMenu,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { EvidenceProgress } from "../evidence-progress.types";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GetEvidenceProgressResponse } from "../evidence-progress.api";
import { EvidenceProgressQuery } from "../evidence-progress.query";
import { EvidenceProgressAPI } from "../evidence-progress.api";
import { Check } from "lucide-react";
import { usePermissions } from "@/hooks";

export const EvidenceProgressBtnSelector: React.FC<{
  defaultValue?: string;
  value?: string;
  onChange?: (value: string, data: EvidenceProgress) => void;
}> = ({ defaultValue, value, onChange }) => {
  const theme = useTheme();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const isDisabled = !hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE);
  const isControlled = useRef(value !== undefined);
  const [stateValue, setStateValue] = useState<string | undefined>(
    defaultValue
  );
  const _value = isControlled.current ? value : stateValue;

  const { data } = useQuery<GetEvidenceProgressResponse>({
    queryKey: EvidenceProgressQuery.queryKeys.list(),
    queryFn: () => EvidenceProgressAPI.get(),
    staleTime: 10000, // 10 seconds
  });

  const handleChange = (value: DropDownItem) => {
    if (value.value === "") {
      setStateValue(undefined);
      onChange?.("", value.data as EvidenceProgress);
      return;
    }
    setStateValue(value.value as string);
    onChange?.(value.value as string, value.data as EvidenceProgress);
  };

  const dropdownOptions: DropDownItem[] = [
    ...(data?.map((progress) => {
      const progressIdString = progress?.progress_name;
      const isSelected = _value === progressIdString;

      return {
        value: progress.progress_name,
        label: progress.progress_name,
        data: progress,
        rightSection: isSelected ? <Check size={14} /> : null,
      };
    }) ?? []),
  ];

  const selectedProgress = data?.find(
    (option) => option.progress_name === _value
  );

  return (
    <DropDownMenu
      data={dropdownOptions}
      variant="contained"
      disabled={isDisabled}
      size="xs"
      onItemSelect={handleChange}
      buttonProps={{
        size: "xs",
        style: {
          backgroundColor: theme.name === "DARK" ? "#303030" : "#f0f0f0",
          color: theme.name === "DARK" ? theme.palette.text.primary : "#000000",
          opacity: selectedProgress ? 1 : 0.75,
          fontWeight: "normal",
          padding: "2px 5px",
          height: "24px",
          textDecoration: "none",
        },
      }}
      dropDownProps={{
        style: {
          width: 160,
        },
      }}
    >
      {selectedProgress ? selectedProgress.progress_name : "Select Progress"}
    </DropDownMenu>
  );
};
